import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import quantService from '../../../services/quantService';

const fmt = (v, n = 4) => (v == null || isNaN(v)) ? '—' : Number(v).toFixed(n);

const STATUS_BADGE = {
  queued:                 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  running:                'bg-brand-gold/10 text-brand-gold',
  done:                   'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  killed:                 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  failed:                 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  cancelled:              'bg-gray-100 text-gray-500',
  interrupted_resumable:  'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
};

const DiscoveryLabPage = () => {
  const navigate = useNavigate();

  const [universes, setUniverses] = useState([]);
  const [universeId, setUniverseId] = useState('');
  const [discoveryStart, setDiscoveryStart] = useState('2010-01-01');
  const [discoveryEnd, setDiscoveryEnd] = useState('2016-12-31');
  const [validationStart, setValidationStart] = useState('2017-01-01');
  const [validationEnd, setValidationEnd] = useState('2020-12-31');
  const [horizon, setHorizon] = useState(21);
  const [labelType, setLabelType] = useState('residual');

  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  // Load universes + recent jobs
  const loadAll = async () => {
    try {
      const [uRes, jRes] = await Promise.all([
        quantService.listUniverses(),
        quantService.listDiscovery(),
      ]);
      setUniverses(uRes.data || []);
      setJobs(jRes.data || []);
      if (!universeId && uRes.data?.length) setUniverseId(uRes.data[0].id);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  useEffect(() => {
    loadAll();
    const t = setInterval(loadAll, 5000);  // keep recent-jobs table fresh
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async () => {
    setError(null);
    setInfo(null);
    if (!universeId) {
      setError('Select a universe.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await quantService.runDiscovery({
        universe_id: universeId,
        discovery_start: discoveryStart,
        discovery_end: discoveryEnd,
        validation_start: validationStart,
        validation_end: validationEnd,
        label_horizon_days: horizon,
        label_type: labelType,
      });
      setInfo(`Job ${data.job_id.slice(0, 8)}… queued. Worker: ${data.worker?.reason || 'spawning'}.`);
      loadAll();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* ───────────── New Discovery Job ─────────────────────────────────── */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            New Discovery Job
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Discovery is the only window where features are selected.
            Validation is consumed by Alphalens (one attempt). Test stays untouched until backtest.
          </p>
        </div>
        <div className="p-4 grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Universe</label>
            <select
              value={universeId}
              onChange={e => setUniverseId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded"
            >
              {universes.length === 0 && <option value="">No universes — create one in admin</option>}
              {universes.map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.member_count} members)</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Label horizon (days)</label>
              <select
                value={horizon}
                onChange={e => setHorizon(Number(e.target.value))}
                className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded"
              >
                {[5, 10, 21, 42].map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Label type</label>
              <select
                value={labelType}
                onChange={e => setLabelType(e.target.value)}
                className="w-full px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded"
              >
                <option value="residual">residual (alpha-only)</option>
                <option value="cs_rank">cross-sectional rank</option>
                <option value="raw">raw return</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Discovery window</label>
            <div className="flex gap-2">
              <input type="date" value={discoveryStart} onChange={e => setDiscoveryStart(e.target.value)}
                     className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
              <input type="date" value={discoveryEnd} onChange={e => setDiscoveryEnd(e.target.value)}
                     className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">Validation window (one-attempt Alphalens)</label>
            <div className="flex gap-2">
              <input type="date" value={validationStart} onChange={e => setValidationStart(e.target.value)}
                     className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
              <input type="date" value={validationEnd} onChange={e => setValidationEnd(e.target.value)}
                     className="flex-1 px-3 py-1.5 text-sm bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-800 rounded" />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Estimated: ~60–90 min on c6a.2xlarge spot ≈ <span className="font-mono">$0.30–$0.55</span>/cycle.
          </div>
          <button
            onClick={submit}
            disabled={submitting || !universeId}
            className="px-4 py-1.5 text-sm bg-brand-gold text-white rounded hover:bg-brand-gold-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Queueing…' : '▶ Run Discovery'}
          </button>
        </div>
        {(error || info) && (
          <div className={`px-4 py-2 text-xs ${error ? 'text-red-500' : 'text-emerald-500'} border-t border-gray-200 dark:border-gray-800`}>
            {error || info}
          </div>
        )}
      </div>

      {/* ───────────── Recent Jobs ────────────────────────────────────────── */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Recent Discovery Jobs
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="text-left px-3 py-2">Job</th>
                <th className="text-left px-3 py-2">Universe</th>
                <th className="text-left px-3 py-2">Phase</th>
                <th className="text-right px-3 py-2 hidden sm:table-cell">Progress</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">ICIR</th>
                <th className="text-right px-3 py-2 hidden md:table-cell">Survivors</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {jobs.length === 0 && (
                <tr><td colSpan={7} className="text-center text-gray-400 py-6 text-sm">No jobs yet.</td></tr>
              )}
              {jobs.map(j => (
                <tr
                  key={j.job_id}
                  onClick={() => navigate(`/trading/quant/jobs/${j.job_id}`)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer border-t border-gray-100 dark:border-gray-800/50"
                >
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                    {j.job_id.slice(0, 8)}…
                  </td>
                  <td className="px-3 py-2 text-xs">{j.universe_name}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                    {j.progress_phase || '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-xs hidden sm:table-cell">
                    {j.progress_pct}%
                  </td>
                  <td className={`px-3 py-2 text-right text-xs hidden md:table-cell font-mono ${
                    (j.icir ?? 0) > 0 ? 'text-emerald-600 dark:text-emerald-400'
                    : (j.icir ?? 0) < 0 ? 'text-red-500 dark:text-red-400' : 'text-gray-400'
                  }`}>
                    {fmt(j.icir, 3)}
                  </td>
                  <td className="px-3 py-2 text-right text-xs hidden md:table-cell">
                    {j.n_features_out ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${
                      STATUS_BADGE[j.status] || 'bg-gray-100'
                    }`}>{j.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryLabPage;
